import React from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

function SkeletonListContainer(props: {
    children: React.ReactNode;
    className?: string;
}) {
    const { children, className } = props;

    return (
        <div className={cn("flex min-w-0 w-full flex-col gap-3", className)}>
            {children}
        </div>
    );
}

function SkeletonCard(props: {
    children: React.ReactNode;
    className?: string;
}) {
    const { children, className } = props;

    return (
        // The boxed placeholder for the profile and series cards (the discussion card became a ROW in
        // SV-135 S2 and has its own placeholder above). Byte-identical until
        // 2026-08-09 to the three shared card skeletons inside `PageNavigationTransition.ts` — which
        // were fixed a day earlier while this sibling was never opened, because nothing about editing
        // the navigation module leads you here. `shadow-md` is a REAL shadow, so this placeholder
        // visibly popped to a flat hairline card the instant the list mounted. Pinned by
        // `components/ui/flatXRestingSurfaces.contract.test.tsx`.
        <div className={cn(
            "w-full overflow-hidden rounded-xl border border-border bg-card",
            className,
        )}>
            {children}
        </div>
    );
}

/**
 * The placeholder for one feed ROW (SV-135 S9, design `2026-09-21-quiet-chrome-feed-visual-weight-design.md`
 * §6.6): the row root's `--divider` bottom edge with no box, radius or fill, and the row's own grid
 * (`FEED_ROW_GRID_CLASS`, written out here rather than imported so this placeholder does not pull the
 * card displayer into every list's loading state). A 40px gutter disc, then the author line, title,
 * excerpt, a media block on the first row, and the action row at the end of the text column. Pinned to
 * `DiscussionCardDisplayer.tsx` by `components/ui/flatXRestingSurfaces.contract.test.tsx`.
 */
function DiscussionCardSkeleton(props: { showMedia?: boolean }) {
    const { showMedia = false } = props;

    return (
        <div className="relative min-w-0 w-full border-b border-divider">
            <div className="grid grid-cols-[2.5rem_minmax(0,1fr)] gap-x-3 px-4 py-3">
                <Skeleton className="size-10 shrink-0 self-start rounded-full" />
                <div className="min-w-0 space-y-2">
                    {/* SV-135 D2: the author line ends in the `…` menu, top right. */}
                    <div className="flex items-center gap-2">
                        <Skeleton className="h-4 w-28" />
                        <Skeleton className="h-4 w-20" />
                        <Skeleton className="ml-auto h-4 w-5 rounded-full" />
                    </div>
                    <Skeleton className="h-5 w-3/4" />
                    <div className="space-y-2">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-11/12" />
                        <Skeleton className="h-4 w-3/4" />
                    </div>
                    {showMedia ? <Skeleton className="mt-3 h-56 w-full rounded-2xl sm:h-72" /> : null}
                    {/* The row's action row (`FEED_ROW_ACTIONS_CLASS` verbatim): left-aligned, spread. */}
                    <div className="mt-1 -ml-2 flex min-w-0 max-w-[28rem] items-center justify-between [&>div]:flex-nowrap [&>div]:gap-1 sm:[&>div]:gap-2 md:[&>div]:gap-3">
                        <Skeleton className="h-8 w-12 rounded-full" />
                        <Skeleton className="h-8 w-20 rounded-full" />
                        <Skeleton className="h-8 w-10 rounded-full" />
                        <Skeleton className="h-8 w-10 rounded-full" />
                    </div>
                </div>
            </div>
        </div>
    );
}

function ProfileCardSkeleton() {
    return (
        <SkeletonCard>
            <div className="space-y-4 px-3 py-3 sm:px-4 sm:py-4">
                <div className="flex items-start gap-3 sm:gap-4">
                    <Skeleton className="size-14 shrink-0 rounded-full sm:size-16" />
                    <div className="min-w-0 flex-1 space-y-2.5">
                        <Skeleton className="h-6 w-40" />
                        <Skeleton className="h-4 w-56 max-w-full" />
                        <div className="flex flex-wrap gap-2">
                            <Skeleton className="h-5 w-24 rounded-full" />
                            <Skeleton className="h-5 w-28 rounded-full" />
                            <Skeleton className="h-5 w-20 rounded-full" />
                        </div>
                    </div>
                </div>
                <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-5/6" />
                </div>
            </div>
            <div className="flex flex-wrap justify-end gap-2 border-t border-border px-3 py-3 sm:px-4">
                <Skeleton className="h-8 w-24 rounded-full" />
                <Skeleton className="h-8 w-24 rounded-full" />
            </div>
        </SkeletonCard>
    );
}

function SeriesCardSkeleton(props: { showMedia?: boolean }) {
    const { showMedia = false } = props;

    return (
        <SkeletonCard>
            {showMedia ? <Skeleton className="h-56 w-full rounded-none sm:h-72" /> : null}
            <div className="space-y-4 px-4 py-4 sm:px-5 sm:py-5">
                <div className="space-y-2">
                    <Skeleton className="h-7 w-2/3" />
                    <Skeleton className="h-4 w-40" />
                </div>
                <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-11/12" />
                    <Skeleton className="h-4 w-3/4" />
                </div>
                <div className="flex flex-wrap gap-2">
                    <Skeleton className="h-5 w-24 rounded-full" />
                    <Skeleton className="h-5 w-28 rounded-full" />
                    <Skeleton className="h-5 w-20 rounded-full" />
                </div>
            </div>
            <div className="flex flex-wrap justify-end gap-2 border-t border-border px-4 py-4 sm:px-5">
                <Skeleton className="h-8 w-24 rounded-full" />
                <Skeleton className="h-8 w-24 rounded-full" />
            </div>
        </SkeletonCard>
    );
}

export function DiscussionCardListSkeleton(props: {
    count?: number;
    className?: string;
}) {
    const { count = 3, className } = props;

    return (
        <SkeletonListContainer className={className}>
            {Array.from({ length: count }, (_, index) => (
                <DiscussionCardSkeleton key={index} showMedia={index === 0} />
            ))}
        </SkeletonListContainer>
    );
}

export function ProfileCardListSkeleton(props: {
    count?: number;
    className?: string;
}) {
    const { count = 3, className } = props;

    return (
        <SkeletonListContainer className={className}>
            {Array.from({ length: count }, (_, index) => (
                <ProfileCardSkeleton key={index} />
            ))}
        </SkeletonListContainer>
    );
}

export function SeriesCardListSkeleton(props: {
    count?: number;
    className?: string;
}) {
    const { count = 2, className } = props;

    return (
        <SkeletonListContainer className={className}>
            {Array.from({ length: count }, (_, index) => (
                <SeriesCardSkeleton key={index} showMedia={index === 0} />
            ))}
        </SkeletonListContainer>
    );
}
